import { useInfiniteQuery, type UseInfiniteQueryOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoListItemDTO } from '@src/application/pascos/dto';

/**
 * Hook: usePendingReviewPascosInfinite
 * Fetches PASCOs that are approved but not yet reviewed using infinite scroll
 *
 * @param params - Filter parameters (excluding skip/limit which are managed by infinite query)
 * @param limit - Maximum number of records to return per page
 * @param options - React Query infinite query options
 * @returns Infinite query result with pending review PASCOs as DTOs
 *
 * @example
 * ```typescript
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading,
 *   error
 * } = usePendingReviewPascosInfinite({
 *     q: 'search term',
 *     university_id: 'uni-123',
 *     semester: '1',
 *     academic_year: '2023/2024'
 * }, 20);
 *
 * // Get all pascos from all pages
 * const allPascos = data?.pages.flatMap(page => page.pascos) || [];
 * ```
 */
export function usePendingReviewPascosInfinite(
    params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
    },
    limit: number = 20,
    options?: Omit<UseInfiniteQueryOptions<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }, Error>, 'queryKey' | 'queryFn' | 'getNextPageParam'>
) {
    const pascoService = getPascoService();

    return useInfiniteQuery({
        queryKey: ['pascos', 'pending-review', 'infinite', { ...params, limit }],
        queryFn: ({ pageParam }) => pascoService.getPendingReviewPascos({
            ...params,
            skip: pageParam as number,
            limit
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            // If there are more pages, return the next skip value
            if (lastPage.hasMore) {
                return allPages.length * limit;
            }
            // No more pages
            return undefined;
        },
        staleTime: 5 * 1000, // 5 seconds
        ...options,
    });
}